import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheckIcon, SearchXIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Panel';
import { Input } from '../ui/Field';
import { ChoiceGroup } from '../ui/ChoiceGroup';
import { EmptyState } from '../ui/EmptyState';
import { ReportControls } from '../trust/ReportControls';
import { groups, people as seedPeople } from '../../data/community';
import type { CommunityPerson, Relationship } from '../../types/community';
const FILTERS = ['Everyone', 'Friends', 'Following', 'Follows you'] as const;
export function PeopleDirectory() {
  const [people, setPeople] = useState<CommunityPerson[]>(seedPeople);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('Everyone');
  function setRelationship(username: string, relationship: Relationship) {
    setPeople(current => current.map(person => person.username === username ? {
      ...person,
      relationship
    } : person));
  }
  function toggleFollow(username: string) {
    setPeople(current => current.map(person => person.username === username ? {
      ...person,
      following: !person.following
    } : person));
  }
  const visible = people.filter(person => {
    if (filter === 'Friends' && person.relationship !== 'friend') return false;
    if (filter === 'Following' && !person.following) return false;
    if (filter === 'Follows you' && !person.followsYou) return false;
    const term = query.trim().replace(/^@/, '').toLowerCase();
    if (term.length > 1 && !`${person.username} ${person.displayName} ${person.headline}`.toLowerCase().includes(term)) {
      return false;
    }
    return true;
  });
  return <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-887" data-mp="1h2xoccbnj892">
      <div className="space-y-4" data-id="element-888" data-mp="1m06bkrbnq2h6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-889" data-mp="1lz4y6g7653wa">
          <div className="sm:max-w-xs" data-id="element-890" data-mp="1kkv6jvlmydnt">
            <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search @username or name" aria-label="Search people" data-id="element-891" data-mp="1khpkkp8azrwb" />
          </div>
          <ChoiceGroup options={FILTERS} value={filter} onChange={setFilter} size="sm" data-id="element-892" data-mp="16l2lafsvopn8" />
        </div>

        {visible.length === 0 ? <div className="rounded-card border border-line bg-surface" data-id="element-893" data-mp="239s0tzh19661">
            <EmptyState icon={<SearchXIcon className="h-4 w-4" data-id="element-895" data-mp="1ydjsb4xccb7u" />} title="Nobody matches" description="Handles are exact — try searching a name instead, or clear the filter." data-id="element-894" data-mp="1yci747mm28ce" />
          </div> : <ul className="space-y-3" data-id="element-896" data-mp="2m11f0m1r0j3y">
            {visible.map(person => <li key={person.id} className="rounded-card border border-line bg-surface px-5 py-4" data-id="element-897" data-mp="2m35mtf2rq29i">
                <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-898" data-mp="2h8hyphbof9vg">
                  <div className="flex min-w-0 gap-3" data-id="element-899" data-mp="3tj8uzua97vt">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[13px] font-semibold text-brand-800" data-id="element-900" data-mp="8pzy5xd7xsah">
                      {person.initials}
                    </span>
                    <div className="min-w-0" data-id="element-901" data-mp="hs99uqk83rpg">
                      <div className="flex flex-wrap items-center gap-2" data-id="element-902" data-mp="mqkfsq7edi2e">
                        <Link to={`/community/people/${person.username}`} className="text-[15px] font-semibold text-ink hover:underline" data-id="element-903" data-mp="mogfsjcmd37e">
                          {person.displayName}
                        </Link>
                        <span className="cz-num text-[13px] text-ink-subtle" data-id="element-904" data-mp="10h5zgh4odw1c">
                          @{person.username}
                        </span>
                        {person.identityVerified && <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-906" data-mp="wt4xp1nyyp1a" />} data-id="element-905" data-mp="1ae1138w8wcju">
                            Verified
                          </Badge>}
                      </div>
                      <p className="mt-1 text-[13px] text-ink-muted" data-id="element-907" data-mp="16j7yho1c17a6">{person.headline}</p>
                      <p className="cz-num mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-ink-subtle" data-id="element-908" data-mp="16l29oamsxl12">
                        <span className="inline-flex items-center gap-1.5" data-id="element-909" data-mp="du0qlyfzn5c9">
                          <UsersIcon className="h-3 w-3" data-id="element-910" data-mp="dwdf6ug94dya" />
                          {person.mutualFriends} mutual
                        </span>
                        <span data-id="element-911" data-mp="ntrkpz0mzibt">
                          {person.sharedGroupIds.length} shared group
                          {person.sharedGroupIds.length === 1 ? '' : 's'}
                        </span>
                        <span data-id="element-912" data-mp="1z6kbp7lls2qt">{person.lastActive}</span>
                        {person.followsYou && <span data-id="element-913" data-mp="1u9liqsdoe1u7">Follows you</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2" data-id="element-914" data-mp="21rjpip4h2ytq">
                    {person.relationship === 'friend' ? <Button size="sm" variant="secondary" onClick={() => setRelationship(person.username, 'none')} data-id="element-915" data-mp="26p1obmorzxo0">
                        Friends
                      </Button> : person.relationship === 'requested' ? <Button size="sm" variant="secondary" onClick={() => setRelationship(person.username, 'none')} data-id="element-916" data-mp="2pi5lhi17csnd">
                        Request sent
                      </Button> : <Button size="sm" variant="primary" icon={<UserPlusIcon className="h-3.5 w-3.5" data-id="element-918" data-mp="3896vd13v28lp" />} onClick={() => setRelationship(person.username, 'requested')} data-id="element-917" data-mp="33hf40ocvryui">
                        Add friend
                      </Button>}
                    <Button size="sm" variant="ghost" onClick={() => toggleFollow(person.username)} data-id="element-919" data-mp="393flzlij5tj9">
                      {person.following ? 'Following' : 'Follow'}
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-3" data-id="element-920" data-mp="2udqg5czht1cn">
                  <p className="text-[12px] text-ink-subtle" data-id="element-921" data-mp="2qdpqsxauv5qt">
                    {person.visibility.programme === 'public' ? 'Programme shown publicly by this person' : person.visibility.programme === 'campus' ? 'Programme shown to people at their campus' : 'Programme shown to friends only'}
                  </p>
                  <ReportControls subject={person.displayName} surface="profile" allowBlock data-id="element-922" data-mp="21bh1ntif9wh9" />
                </div>
              </li>)}
          </ul>}
      </div>

      <div className="space-y-6" data-id="element-923" data-mp="2bbhm5is12in1">
        <Panel title="How people find you" data-id="element-924" data-mp="2b83kydv3ofh6">
          <p className="text-[13px] leading-6 text-ink-muted" data-id="element-925" data-mp="2g9j583pgehyi">
            Your handle <span className="cz-num font-medium text-ink" data-id="element-926" data-mp="1ilr4izr6sym6">@amarachi</span> is your public
            identity across Campuzon — community, marketplace and money requests all address it.
          </p>
          <p className="mt-2 text-[13px] leading-6 text-ink-muted" data-id="element-927" data-mp="1sjyj5by074l0">
            It is never your financial account identifier, and discoverability is controlled from your
            profile privacy settings.
          </p>
          <Link to="/profile" className="mt-3 inline-block" data-id="element-928" data-mp="zvsw2ijh93i0">
            <Button size="sm" variant="secondary" data-id="element-929" data-mp="zt6o4hsoclz9">
              Privacy settings
            </Button>
          </Link>
        </Panel>
        <Panel title="Shared groups" bodyClassName="p-0" data-id="element-930" data-mp="2izsqy9wcnch5">
          <ul className="divide-y divide-line" data-id="element-931" data-mp="2j2dur4g7k2hj">
            {groups.filter(group => group.joined).map(group => <li key={group.id} className="px-5 py-3" data-id="element-932" data-mp="2szrwrz1dwoqb">
                  <p className="text-[13px] font-medium text-ink" data-id="element-933" data-mp="2nznhgcc7q01o">{group.name}</p>
                  <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-934" data-mp="2o1qe3f7verfd">
                    {group.members} members · {group.kind}
                  </p>
                </li>)}
          </ul>
        </Panel>
      </div>
    </div>;
}